import { Item, ItemText, ItemIndicator } from '@radix-ui/react-select';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import { Icon } from '../Icon';
import { getTypographicStyles } from '../../private/helpers/getTypographicStyles/getTypographicStyles';

export type SelectOptionProps = {
  /**
   * The visual content of the select
   */
  children: ReactNode;
  /**
   * The value to set. Leave this empty to define a placeholder option.
   */
  value: string;
  /**
   * When true, this option will not be selectable
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * Optional content to display in the trigger when the option is selected. If undefined, it will show the `children`
   * prop instead.
   */
  selectedDisplayValue?: ReactNode;
  /**
   * Optional text used for typeahead purposes. By default the typeahead behavior will use the `.textContent` of the Option.
   * Use this when the content is complex, or you have non-textual content inside.
   */
  textValue?: string;
  /**
   * Vertical alignment of the checkmark icon when this option is selected.
   */
  checkmarkVerticalAlign?: 'center' | 'top';
};

const StyledItem = styled(Item)<{
  $checkmarkVerticalAlign: SelectOptionProps['checkmarkVerticalAlign'];
}>`
  ${getTypographicStyles('label3')}
  align-items: ${({ $checkmarkVerticalAlign }) =>
    $checkmarkVerticalAlign === 'center' ? 'center' : 'flex-start'};
  background-color: transparent;
  display: flex;
  padding: var(--wui-select-option-padding);
  justify-content: space-between;
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  border-radius: var(--wui-border-radius-01);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background-color: var(--wui-select-option-bg-hover);
    outline: none;
  }

  &[aria-disabled='true'] {
    color: var(--wui-color-text-disabled);
  }

  &[aria-disabled='true']:hover {
    background-color: transparent;
  }
`;

export const SelectOption = forwardRef<HTMLDivElement, SelectOptionProps>(
  (
    { children, selectedDisplayValue, checkmarkVerticalAlign = 'center', ...props },
    forwardedRef,
  ) => {
    return (
      <StyledItem
        {...props}
        ref={forwardedRef}
        $checkmarkVerticalAlign={checkmarkVerticalAlign}
      >
        {isNotNil(selectedDisplayValue) ? (
          <div>
            {children}
            <div style={{ display: 'none' }}>
              <ItemText>{selectedDisplayValue}</ItemText>
            </div>
          </div>
        ) : (
          <ItemText>{children}</ItemText>
        )}

        <ItemIndicator>
          <Icon
            size="md"
            type="checkmark"
          />
        </ItemIndicator>
      </StyledItem>
    );
  },
);

SelectOption.displayName = 'SelectOption';
